import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { PredictionItem, UsePlaceAutocompleteControllerProps } from './PlaceAutocomplete.types'
import { parseAddressComponents } from './PlaceAutocomplete.utils'
import { DEFAULT_DEBOUNCE_MS, MIN_INPUT_LENGTH } from './PlaceAutocomplete.data'

export const usePlaceAutocompleteController = ({
  value,
  onChange,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  countries,
  onAddressSelect,
}: UsePlaceAutocompleteControllerProps) => {
  const places = useMapsLibrary('places')
  const [predictions, setPredictions] = useState<PredictionItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  const debounceRef = useRef<number | null>(null)

  const componentRestrictions = useMemo(() => {
    if (!countries || countries.length === 0) return undefined
    return { country: countries }
  }, [countries])

  const onSelectPrediction = useCallback(
    async (prediction: PredictionItem, onValueChange: (value: string) => void) => {
      if (!places) return

      onValueChange(prediction.title)

      try {
        const place = new places.Place({
          id: prediction.placeId,
        })

        await place.fetchFields({
          fields: ['addressComponents', 'id', 'location', 'formattedAddress', 'displayName'],
        })

        if (!place.addressComponents) {
          setError('Failed to fetch place details')
          return
        }

        const convertedComponents: google.maps.GeocoderAddressComponent[] =
          place.addressComponents.map((component) => ({
            long_name: component.longText || '',
            short_name: component.shortText || '',
            types: component.types || [],
          }))

        const parsed = parseAddressComponents(convertedComponents)
        onAddressSelect?.(parsed)
        if (places) sessionTokenRef.current = new places.AutocompleteSessionToken()
        setPredictions([])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch place details')
      }
    },
    [onAddressSelect, places],
  )

  const onSelect = useCallback(
    (prediction: PredictionItem) => {
      const updateValue = (newValue: string) => {
        onChange?.(newValue)
      }
      onSelectPrediction(prediction, updateValue)
    },
    [onSelectPrediction, onChange],
  )

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      onChange?.(newValue)
    },
    [onChange],
  )

  // Fetch predictions with debounce
  const fetchPredictions = useCallback(() => {
    if (!places) return

    if (debounceRef.current) window.clearTimeout(debounceRef.current)

    if (!value || value.trim().length < MIN_INPUT_LENGTH) {
      setPredictions([])
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    debounceRef.current = window.setTimeout(async () => {
      try {
        const request = {
          input: value,
          sessionToken: sessionTokenRef.current || undefined,
          includedRegionCodes: componentRestrictions?.country,
        }

        const { suggestions } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

        setLoading(false)

        if (!suggestions || suggestions.length === 0) {
          setPredictions([])
          return
        }

        const mapped: PredictionItem[] = suggestions
          .filter((s) => s.placePrediction)
          .map((s) => {
            const p = s.placePrediction!
            return {
              placeId: p.placeId,
              title: p.mainText?.text || p.text?.text || '',
              location: p.secondaryText?.text || '',
            }
          })

        setPredictions(mapped)
      } catch (err) {
        setLoading(false)
        setError(err instanceof Error ? err.message : 'Failed to fetch suggestions')
        setPredictions([])
      }
    }, debounceMs)
  }, [value, debounceMs, componentRestrictions, places])

  // Initialize session token
  useEffect(() => {
    if (!places) return
    if (!sessionTokenRef.current) sessionTokenRef.current = new places.AutocompleteSessionToken()
  }, [places])

  useEffect(() => {
    fetchPredictions()

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [fetchPredictions])

  return {
    predictions,
    loading,
    error,
    onSelectPrediction,
    onSelect,
    onInputChange,
  }
}
